<script setup>
  import { provide, onMounted, onUnmounted, computed, nextTick, watch } from "vue";
  import { useToastsStore } from "@/stores/toastsStore";

  import { capitalize } from "@utils/toolBox";
  import SurveyEdit from "@/components/Survey/Edit/SurveyEdit.vue";
  import SurveyReview from "@/components/Survey/Review/SurveyReview.vue";
  import SurveyTest from "@/components/Survey/Test/SurveyTest.vue";
  import { useEventBus } from "@vueuse/core";
  import { useRoute, useRouter } from "vue-router";
  import { storeToRefs } from "pinia";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useCardsStore } from "@stores/survey/cards";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useSectionsStore } from "@stores/survey/sections";
  import { useSurveySimulation } from "@utils/composables/surveySimulation";
  import { useUnsavedChangesGuard } from "@utils/composables/useUnsavedChangesGuard";

  const props = defineProps({
    survey: { type: Object, required: true },
    questionCount: { type: Number, required: true },
    cards: { type: Array, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    cardTypesGroups: { type: Array, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
  });

  const surveyCardsStore = useSurveyCardsStore();
  const { switchBetweenTabs, activePane } = storeToRefs(surveyCardsStore);
  const { setActivePane, selectCurrentCard } = surveyCardsStore;
  const { currentToken } = storeToRefs(useCurrentCardStore());

  const { hasUnsavedChanges: hasCardsChanged } = storeToRefs(useCardsStore());
  const { hasUnsavedChanges: hasSectionsChanged } = storeToRefs(useSectionsStore());
  useUnsavedChangesGuard(computed(() => hasCardsChanged.value || hasSectionsChanged.value));

  // Get current card from simulation to sync when switching from Test to Edit
  const { currentCard: simulationCurrentCard } = useSurveySimulation(props.survey, {});

  const questionEventBus = useEventBus("question");
  provide("questionEventBus", questionEventBus);

  questionEventBus.on((eventDescriptor) => {
    const { command } = eventDescriptor;
    const actions = { changeTab: redirectToEdit };
    actions[command]?.();
  });

  function redirectToEdit() {
    setActivePane("Edit");
  }

  const toast = useToastsStore();
  const route = useRoute();
  const router = useRouter();
  const currentHashRoute = computed(() => route.hash);

  onMounted(async () => {
    await nextTick(() => {
      if (currentHashRoute.value === "#review") setActivePane("Review");
      else if (currentHashRoute.value === "#test") setActivePane("Test");
    });
  });

  onUnmounted(() => {
    switchBetweenTabs.value = false;
    setActivePane("Edit");
  });

  const cardQuery = () => (activePane.value === "Edit" && currentToken.value ? { card: currentToken.value } : {});

  const isEditPaneSettled = () => activePane.value === "Edit" && ["#edit", ""].includes(route.hash);

  const writeCardToUrl = (token) => {
    const url = new URL(window.location.href);
    url.searchParams.set("card", token);
    window.history.replaceState(window.history.state, "", url);
  };

  watch(currentToken, (token) => {
    if (token && isEditPaneSettled()) writeCardToUrl(token);
  });

  watch(activePane, (activeTab, previousTab) => {
    // When switching from Test to Edit, navigate to the current simulation card
    if (previousTab === "Test" && activeTab === "Edit" && simulationCurrentCard.value) {
      selectCurrentCard({ token: simulationCurrentCard.value });
    }

    const hashMap = { Edit: "#edit", Review: "#review", Test: "#test" };
    router.push({ name: "SurveyQuestions", hash: hashMap[activeTab] || "#edit", query: cardQuery() });
  });

  watch(
    () => props.survey.errors,
    (errors) => {
      errors?.forEach((error) => {
        if (error.path === "survey") {
          toast.error(capitalize(error.message));
        }
      });
    },
  );
</script>

<template>
  <div>
    <SurveyEdit
      v-if="activePane === 'Edit'"
      :survey="survey"
      :cards="cards"
      :question-count="questionCount"
      :card-sub-types-infos="cardSubTypesInfos"
      :is-card-filtered-out="isCardFilteredOut"
      :is-filter-active="isFilterActive"
      :switch-between-tabs="switchBetweenTabs" />
    <SurveyReview
      v-if="activePane === 'Review'"
      :cards="cards"
      :question-count="questionCount"
      :card-sub-types-infos="cardSubTypesInfos"
      :card-types-groups="cardTypesGroups"
      :is-card-filtered-out="isCardFilteredOut"
      :is-filter-active="isFilterActive"
      :switch-between-tabs="switchBetweenTabs" />
    <SurveyTest
      v-if="activePane === 'Test'"
      :survey="survey"
      :cards="cards"
      :question-count="questionCount"
      :card-sub-types-infos="cardSubTypesInfos"
      :is-card-filtered-out="isCardFilteredOut"
      :is-filter-active="isFilterActive" />
  </div>
</template>
